import { z } from 'zod';
import { UserRoles, UserStatuses } from '../statuses';
import { optionalTrimmed, trimmed, uuid } from './common';
/**
 * パスワード方針（docs/11-security.md 2.1）：
 * 12 文字以上、英大・英小・数字・記号のうち 3 種類以上。
 */
export const PASSWORD_MIN_LENGTH = 12;
export const passwordSchema = z
    .string()
    .min(PASSWORD_MIN_LENGTH, `${PASSWORD_MIN_LENGTH} 文字以上で入力してください`)
    .max(200, '200 文字以内で入力してください')
    .refine((v) => {
    const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/].filter((re) => re.test(v)).length;
    return classes >= 3;
}, { message: '英大文字・英小文字・数字・記号のうち 3 種類以上を含めてください' });
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .email('メールアドレスの形式が正しくありません')
    .max(254);
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'パスワードを入力してください').max(200),
});
export const loginResponseSchema = z.object({
    accessToken: z.string(),
    expiresIn: z.number().int(),
    user: z.object({
        id: uuid,
        name: z.string().nullable(),
        email: z.string(),
        role: z.enum(['admin', 'manager', 'member']),
    }),
});
export const passwordResetRequestSchema = z.object({ email: emailSchema });
export const passwordResetConfirmSchema = z
    .object({
    token: z.string().min(10).max(200),
    password: passwordSchema,
    passwordConfirm: z.string(),
})
    .refine((v) => v.password === v.passwordConfirm, {
    message: 'パスワードが一致しません',
    path: ['passwordConfirm'],
});
export const acceptInvitationSchema = z
    .object({
    token: z.string().min(10).max(200),
    name: trimmed(80, '氏名を入力してください'),
    password: passwordSchema,
    passwordConfirm: z.string(),
})
    .refine((v) => v.password === v.passwordConfirm, {
    message: 'パスワードが一致しません',
    path: ['passwordConfirm'],
});
export const inviteUserSchema = z.object({
    email: emailSchema,
    role: z.enum(UserRoles.values).pipe(z.enum(['admin', 'manager', 'member'])),
    name: optionalTrimmed(80),
});
export const updateUserSchema = z
    .object({
    role: z.enum(['admin', 'manager', 'member']).optional(),
    status: z.enum(['active', 'suspended']).optional(),
    name: optionalTrimmed(80),
    /** 停止・削除時に担当企業がある場合の引き継ぎ先 */
    handoverToUserId: uuid.optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: '変更内容がありません' });
export const updateMeSchema = z.object({
    name: optionalTrimmed(80),
    notificationSettings: z
        .object({
        email: z.boolean().optional(),
        inApp: z.boolean().optional(),
        digestHour: z.number().int().min(0).max(23).optional(),
    })
        .optional(),
    /** サイドバー折りたたみ等の UI 設定 */
    uiPreferences: z.record(z.string(), z.unknown()).optional(),
});
export const meSchema = z.object({
    user: z.object({
        id: uuid,
        email: z.string(),
        name: z.string().nullable(),
        role: z.enum(['admin', 'manager', 'member']),
        status: z.enum(['invited', 'active', 'suspended']),
        lastLoginAt: z.string().nullable(),
        notificationSettings: z.record(z.string(), z.unknown()).nullable(),
        uiPreferences: z.record(z.string(), z.unknown()).nullable(),
    }),
    organization: z.object({
        id: uuid,
        name: z.string(),
        timezone: z.string(),
    }),
    /** 画面表示の補助。判定の根拠にはしない（docs/11-security.md 3.1） */
    permissions: z.array(z.string()),
    settings: z.record(z.string(), z.unknown()),
});
export const userSummarySchema = z.object({
    id: uuid,
    email: z.string(),
    name: z.string().nullable(),
    role: z.enum(['admin', 'manager', 'member']),
    status: z.enum(UserStatuses.values),
    lastLoginAt: z.string().nullable(),
    assignedCompanyCount: z.number().int().optional(),
    invitationExpiresAt: z.string().nullable().optional(),
    createdAt: z.string(),
});
export const deleteUserSchema = z.object({
    handoverToUserId: uuid.optional(),
});
export const PERMISSIONS = [
    'users.manage',
    'integrations.manage',
    'settings.manage',
    'settings.view',
    'suppressions.manage',
    'products.manage',
    'collections.run',
    'lists.manage.all',
    'companies.assign',
    'companies.edit.all',
    'templates.share',
    'campaigns.manage',
    'sends.single.all',
    'analytics.view.all',
    'suggestions.view',
    'logs.view',
    'logs.view.limited',
    'costs.view',
    'export.all',
    'import.run',
];
/** docs/01-requirements.md 3.2 権限マトリクス */
export const PERMISSIONS_BY_ROLE = {
    admin: [...PERMISSIONS],
    manager: [
        'settings.view',
        'suppressions.manage',
        'products.manage',
        'collections.run',
        'lists.manage.all',
        'companies.assign',
        'companies.edit.all',
        'templates.share',
        'campaigns.manage',
        'sends.single.all',
        'analytics.view.all',
        'suggestions.view',
        'logs.view.limited',
        'costs.view',
        'export.all',
        'import.run',
    ],
    member: ['collections.run', 'suggestions.view'],
};
//# sourceMappingURL=auth.js.map